/**
 * Display helpers for library contacts (migration 0018).
 *
 * A contact carries four independent type flags rather than one role, so
 * "what is this person" is a computed string in several places — the picker,
 * the master-contractor dropdown, the contact list. Extracted so there's one
 * definition of the order and separator rather than three.
 */

import type { DesignerContactRow } from '@/types/database';

/** Only the flags, in a fixed order. `''` when none are set. */
export function contactTypeLabel(c: Pick<DesignerContactRow,
  'is_client' | 'is_contractor' | 'is_subcontractor' | 'is_vendor'>): string {
  return [
    c.is_client && 'Client',
    c.is_contractor && 'Contractor',
    c.is_subcontractor && 'Subcontractor',
    c.is_vendor && 'Vendor',
  ].filter(Boolean).join(' · ');
}

/** Company and types, for the second line of a picker row. `''` when neither. */
export function contactSummary(c: Pick<DesignerContactRow,
  'company' | 'is_client' | 'is_contractor' | 'is_subcontractor' | 'is_vendor'>): string {
  return [c.company, contactTypeLabel(c)].filter(Boolean).join(' · ');
}

/**
 * Label for the master-contractor dropdown.
 *
 * The dropdown deliberately offers EVERY project contact rather than only
 * those flagged Contractor: filtering would leave the list empty whenever the
 * designer hasn't ticked that box yet, with no explanation of why. Showing the
 * flags alongside the name makes the right choice obvious without the trap.
 */
export function contactOptionLabel(c: Pick<DesignerContactRow,
  'name' | 'company' | 'is_client' | 'is_contractor' | 'is_subcontractor' | 'is_vendor'>): string {
  const detail = contactSummary(c);
  return detail ? `${c.name} — ${detail}` : c.name;
}

/** The project's master contractor, or null when unset or no longer on it. */
export function findMasterContractor(
  contacts: DesignerContactRow[],
  masterContractorId: string | null | undefined,
): DesignerContactRow | null {
  if (!masterContractorId) return null;
  return contacts.find((c) => c.id === masterContractorId) ?? null;
}

/** Searchable haystack for the picker's filter box. */
export function contactSearchKey(c: Pick<DesignerContactRow,
  'name' | 'company' | 'email' | 'phone'
  | 'is_client' | 'is_contractor' | 'is_subcontractor' | 'is_vendor'>): string {
  return [c.name, c.company, c.email, c.phone, contactTypeLabel(c)]
    .filter(Boolean).join(' ').toLowerCase();
}
